import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type VideoId = string | number;

interface ActiveVideoActions {
	join: (id: VideoId) => void;
	leave: (id: VideoId) => void;
}

interface Pool {
	active: VideoId[];
	pending: VideoId[];
}

const EMPTY_POOL: Pool = { active: [], pending: [] };

// Kept separate from state below so `actions`'s identity stays stable while the pool churns.
const ActiveVideoActionsContext = createContext<ActiveVideoActions | null>(null);
const ActiveVideoStateContext = createContext<Pool>(EMPTY_POOL);

export const DEFAULT_MAX_VIDEOS = 3;

export interface ActiveVideoProviderProps {
	children: React.ReactNode;
	maxVideos?: number;
}

export const ActiveVideoProvider: React.FC<ActiveVideoProviderProps> = ({ children, maxVideos = DEFAULT_MAX_VIDEOS }) => {
	const [pool, setPool] = useState<Pool>(EMPTY_POOL);

	const join = useCallback(
		(id: VideoId) => {
			setPool((prev) => {
				if (prev.active.includes(id) || prev.pending.includes(id)) return prev;
				if (prev.active.length < maxVideos) return { active: [...prev.active, id], pending: prev.pending };
				const pending = [...prev.pending, id];
				// A full batch of new videos is in view - swap them in for the ones currently playing.
				return pending.length >= maxVideos ? { active: pending, pending: [] } : { active: prev.active, pending };
			});
		},
		[maxVideos],
	);

	const leave = useCallback(
		(id: VideoId) => {
			setPool((prev) => {
				const pending = prev.pending.filter((pendingId) => pendingId !== id);
				if (!prev.active.includes(id)) return pending.length === prev.pending.length ? prev : { active: prev.active, pending };
				const active = prev.active.filter((activeId) => activeId !== id);
				// A video actually left the viewport (not removed) - let the oldest waiter take its free slot right away.
				if (active.length < maxVideos && pending.length > 0) {
					const [next, ...rest] = pending;
					return { active: [...active, next], pending: rest };
				}
				return { active, pending };
			});
		},
		[maxVideos],
	);

	const actions = useMemo(() => ({ join, leave }), [join, leave]);

	return (
		<ActiveVideoActionsContext.Provider value={actions}>
			<ActiveVideoStateContext.Provider value={pool}>{children}</ActiveVideoStateContext.Provider>
		</ActiveVideoActionsContext.Provider>
	);
};

// Joins/leaves the shared pool as a side effect, and returns whether `id` holds one of its slots.
// No provider or no `id` means uncoordinated - always allowed to play.
export const useCanPlayVideo = (id: VideoId | undefined, wantsToPlay: boolean): boolean => {
	const actions = useContext(ActiveVideoActionsContext);
	const { active } = useContext(ActiveVideoStateContext);

	useEffect(() => {
		if (!actions || id === undefined) return undefined;
		if (wantsToPlay) {
			actions.join(id);
		} else {
			actions.leave(id);
		}
		return () => actions.leave(id);
	}, [actions, id, wantsToPlay]);

	if (!actions || id === undefined) return wantsToPlay;
	return active.includes(id);
};

// Whether `id` is queued for the next batch - a good moment to start buffering ahead of its turn.
export const useIsPendingVideo = (id: VideoId | undefined): boolean => {
	const { pending } = useContext(ActiveVideoStateContext);
	return id !== undefined && pending.includes(id);
};

// Lets a user manually claim a slot for `id` (e.g. clicking play on a paused card) - goes through
// the same pool as automatic joins, so it can remove the oldest active video instead of bypassing the cap.
export const useRequestPlay = (id: VideoId | undefined): (() => void) => {
	const actions = useContext(ActiveVideoActionsContext);
	return useCallback(() => {
		if (actions && id !== undefined) actions.join(id);
	}, [actions, id]);
};
